import { getId, maybeAppendCopyToText } from "@indietabletop/appkit";
import { differenceBy, uniqBy } from "es-toolkit";
import type { Greathelm } from "../../types/lib/index.ts";
import {
  ActionEffect,
  type DefenseEffect,
  type SpecialRuleEffect,
} from "./Effect";
import type { AnyArmorRoll, ItemProfileType, Ref } from "./Ruleset";
import type { ShieldProfile } from "./ShieldProfile.ts";
import type { Warband } from "./Warband";
import { WeakMapCache } from "./WeakMapCache";
import type { WeaponProfile } from "./WeaponProfile.ts";

export type KnightIssue = {
  type: "INVALID_EQUIPMENT_COMBINATION";
  equipment: (WeaponProfile | ShieldProfile)[];
};

export type DefenseOption<T> = {
  type: T;
  modifier: number;
};

export type AnyDefenseOption =
  | DefenseOption<"BLOCK">
  | DefenseOption<"PARRY">
  | DefenseOption<"RIPOSTE">
  | DefenseOption<"DODGE">;

export type MoveAction<T> = {
  type: T;
  modifier: number;
};

export type AnyMoveAction =
  | MoveAction<"RUN">
  | MoveAction<"WALK">
  | MoveAction<"STEP">;

export type AttackAction<T> = {
  type: T;
  modifier: number;
};

export type AnyAttackAction =
  | AttackAction<"BASH">
  | AttackAction<"LIGHT_MELEE_ATTACK">
  | AttackAction<"HEAVY_MELEE_ATTACK">
  | AttackAction<"LIGHT_RANGED_ATTACK">
  | AttackAction<"HEAVY_RANGED_ATTACK">;

export class Knight {
  warband: Warband;
  data: Greathelm.KnightData;
  private cache: WeakMapCache;

  constructor(knightlyOrder: Warband, data: Greathelm.KnightData) {
    this.warband = knightlyOrder;
    this.data = data;
    this.cache = new WeakMapCache();
  }

  get id() {
    return this.data.id;
  }

  get count() {
    return this.data.count;
  }

  get name() {
    if (this.data.name) {
      return this.data.name;
    }

    const weapon = this.weapons.at(0)?.valueOrNull();

    if (!weapon) {
      return "Unarmed Knight";
    }

    return `Knight with ${weapon.name}`;
  }

  get summary() {
    return this.equipment
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value)
      .map((item) => item.name)
      .join(", ");
  }

  get armor() {
    const armorProfiles = this.ruleset.armorProfiles;
    return this.cache.get(this.data.armor, (ref) => armorProfiles.get(ref.id));
  }

  get weapons() {
    const weaponProfiles = this.ruleset.weaponProfiles;
    return this.cache.get(this.data.weapons, (refs) => {
      return refs.map((ref) => weaponProfiles.get(ref.id));
    });
  }

  get shields() {
    const shieldProfiles = this.ruleset.shieldProfiles;
    return this.cache.get(this.data.shields, (refs) => {
      return refs.map((ref) => shieldProfiles.get(ref.id));
    });
  }

  get upgrades() {
    const upgradeProfiles = this.ruleset.upgradeProfiles;
    return this.cache.get(this.data.upgrades, (refs) => {
      return refs.map((ref) => upgradeProfiles.get(ref.id));
    });
  }

  get equipment() {
    return [
      this.armor,
      ...this.weapons,
      ...this.shields,
      ...this.wearables,
      ...this.items,
      ...this.upgrades,
    ];
  }

  get effects() {
    return this.equipment
      .filter((lookup) => lookup.isSuccess)
      .flatMap((lookup) => lookup.value.effects);
  }

  getModifierFor(targetRef: Ref) {
    return this.effects.reduce((acc, effect) => {
      switch (effect.type) {
        case "ADD_ACTION_MODIFIER": {
          if (Array.isArray(effect.action)) {
            return effect.action.some((ref) => ref.id === targetRef.id)
              ? acc + effect.value
              : acc;
          }

          return effect.action.id === targetRef.id ? acc + effect.value : acc;
        }

        case "ADD_DEFENSE_MODIFIER": {
          if (Array.isArray(effect.defense)) {
            return effect.defense.some((ref) => ref.id === targetRef.id)
              ? acc + effect.value
              : acc;
          }

          return effect.defense.id === targetRef.id ? acc + effect.value : acc;
        }

        default: {
          return acc;
        }
      }
    }, 0);
  }

  get attackActions() {
    const added = this.effects
      .filter((e): e is ActionEffect => e.type === "GAIN_ACTION")
      .flatMap((e) => e.action);

    const actions = uniqBy(
      [...this.ruleset.defaultActions.filter((a) => a.hasClashTest), ...added],
      (i) => i.id,
    );

    const removed = new Set(
      this.effects
        .filter((e): e is ActionEffect => e.type === "REMOVE_ACTION")
        .flatMap((e) => e.action)
        .map((action) => action.id),
    );

    return actions
      .filter((action) => !removed.has(action.id))
      .map((action) => {
        return { action, modifier: this.getModifierFor(action) };
      });
  }

  get armorRolls(): AnyArmorRoll[] {
    return this.armor.valueOrNull()?.rolls ?? [];
  }

  get defenseOptions() {
    const remove = this.effects
      .filter((e): e is DefenseEffect => e.type === "REMOVE_DEFENSE")
      .flatMap((e) => e.defense);

    return differenceBy(
      this.ruleset.defenses.toArray(),
      remove,
      (item) => item.id,
    ).map((defense) => {
      return { defense, modifier: this.getModifierFor(defense) };
    });
  }

  get specialRules() {
    return this.effects.filter(
      (e): e is SpecialRuleEffect => e.type === "ADD_SPECIAL_RULE",
    );
  }

  get attackSpecialRules() {
    return this.specialRules.filter((r) => r.group === "ATTACK");
  }

  get defenseSpecialRules() {
    return this.specialRules.filter((r) => r.group === "DEFENSE");
  }

  get armorSpecialRules() {
    return this.specialRules.filter((r) => r.group === "ARMOR");
  }

  get moveActions() {
    return this.ruleset.defaultActions
      .filter((action) => action.unit === "INCHES")
      .map((action) => {
        return {
          action,
          modifier: this.getModifierFor(action),
        };
      });
  }

  get items() {
    const itemProfiles = this.ruleset.itemProfiles;

    return this.cache.get(this.data.items, (items) => {
      return items.map((ref) => itemProfiles.get(ref.id));
    });
  }

  get wearables() {
    const wearableProfiles = this.ruleset.wearableProfiles;

    return this.cache.get(this.data.wearables, (items) => {
      return items.map((ref) => wearableProfiles.get(ref.id));
    });
  }

  get wearablesCount() {
    return this.wearables.filter((l) => l.isSuccess).length;
  }

  get consumablesCount() {
    return this.items
      .filter((lookup) => lookup.isSuccess)
      .map((item) => item.value)
      .filter((item) => {
        const consumable: ItemProfileType[] = [
          "CONSUMABLE_INSTANT",
          "CONSUMABLE_BUFF",
          "CONSUMABLE_TOSSED",
        ];

        return consumable.includes(item.type);
      }).length;
  }

  get usablesCount() {
    return this.items
      .filter((lookup) => lookup.isSuccess)
      .map((item) => item.value)
      .filter((item) => item.type === "USABLE").length;
  }

  get passiveRules() {
    return this.effects
      .filter((e): e is SpecialRuleEffect => e.type === "ADD_SPECIAL_RULE")
      .filter((e) => e.group === "PASSIVE");
  }

  get ruleset() {
    return this.warband.ruleset;
  }

  get profile() {
    return this.ruleset.knightProfiles.get(this.data.profile.id).valueOrThrow();
  }

  get damageLimit() {
    return this.profile.damageLimit;
  }

  get momentumLimit() {
    return this.effects.reduce((acc, effect) => {
      if (effect.type === "ADD_MOMENTUM_LIMIT_MODIFIER") {
        return acc + effect.value;
      }

      return acc;
    }, this.profile.momentumLimit);
  }

  get hasTwoHandedWeapon() {
    return this.weapons
      .filter((l) => l.isSuccess)
      .map((l) => l.value)
      .some((w) => w.type === "TWO_HANDED_RANGED" || w.type === "TWO_HANDED");
  }

  get hasShield() {
    return this.shields.filter((l) => l.isSuccess).length > 0;
  }

  get issues() {
    const issues: KnightIssue[] = [];

    if (this.hasTwoHandedWeapon && this.hasShield) {
      issues.push({
        type: "INVALID_EQUIPMENT_COMBINATION",
        equipment: [
          ...this.weapons.filter((l) => l.isSuccess).map((l) => l.value),
          ...this.shields.filter((l) => l.isSuccess).map((l) => l.value),
        ],
      });
    }

    return issues;
  }

  duplicate() {
    return this.warband.addKnight({
      ...this.data,
      name: maybeAppendCopyToText(this.data.name),
    });
  }

  clone(overrides?: Partial<Greathelm.KnightData>) {
    return this.warband
      .clone({
        knights: this.warband.data.knights.map((knight) => {
          return knight.id === this.id ? { ...knight, ...overrides } : knight;
        }),
      })
      .getKnight(this.id)
      .valueOrThrow();
  }

  static from(
    knightlyOrder: Warband,
    overrides?: Partial<Greathelm.KnightData>,
  ) {
    const defaultKnight = knightlyOrder.ruleset.defaultKnight;

    return new Knight(knightlyOrder, {
      ...defaultKnight,
      ...overrides,
      id: getId(),
      count: 1,
    });
  }
}
